import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Copy, Check, Plug, RefreshCw, Loader2, ShieldCheck, WifiOff, LifeBuoy } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const TrackingConnect = ({ clientId, accent = "#A3E635", onConnected, variant = "button", label }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkedMsg, setCheckedMsg] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [installTicket, setInstallTicket] = useState(null);
  const pollRef = useRef(null);

  const q = clientId ? `?client_id=${clientId}` : "";
  const snippet = status?.tracking_key ? `<script async src="${BACKEND}/api/track/t.js?k=${status.tracking_key}"></script>` : "";

  const openModal = async () => {
    setOpen(true); setLoading(true); setCheckedMsg(null);
    try { const r = await api.post(`/cockpit/tracking/setup${q}`); setStatus(r.data); }
    catch { toast.error(t("Konnte nicht geladen werden.")); }
    finally { setLoading(false); }
  };
  const close = () => { setOpen(false); if (pollRef.current) clearInterval(pollRef.current); };

  const refreshStatus = async () => {
    try {
      const r = await api.get(`/cockpit/tracking/status${q}`); setStatus(r.data);
      if (r.data.connected && pollRef.current) clearInterval(pollRef.current);
    } catch { /* still polling */ }
  };
  useEffect(() => {
    if (open && status && !status.connected) {
      pollRef.current = setInterval(refreshStatus, 5000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [open, status?.connected]);

  const copy = async () => { try { await navigator.clipboard.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ } };
  const verify = async () => {
    setVerifying(true); setCheckedMsg(null);
    try {
      const r = await api.post(`/cockpit/tracking/verify${q}`); setStatus(r.data);
      if (r.data.connected) { toast.success(t("Verbunden! Ihre Live-Analyse ist aktiv.")); onConnected && onConnected(); }
      else setCheckedMsg(t("Code noch nicht gefunden. Bitte einfügen und veröffentlichen — wir prüfen auch automatisch, sobald der erste Besucher kommt."));
    } catch { toast.error(t("Prüfung fehlgeschlagen.")); }
    finally { setVerifying(false); }
  };

  const requestInstall = async () => {
    setRequesting(true);
    try {
      const r = await api.post(`/cockpit/tracking/request-install${q}`, { snippet });
      setInstallTicket(r.data.ticket_no || true);
      toast.success(r.data.already_open ? t("Ihre Einbau-Anfrage ist bereits offen — KEWAG kümmert sich darum.") : t("KEWAG kümmert sich darum — Supportfall wurde erstellt."));
    } catch { toast.error(t("Anfrage konnte nicht gesendet werden.")); }
    finally { setRequesting(false); }
  };

  const Trigger = variant === "button" ? (
    <button onClick={openModal} data-testid="tracking-connect-btn"
      className="inline-flex items-center gap-2 font-bold text-black rounded-xl px-4 py-2.5 text-sm active:scale-95 transition-transform shrink-0" style={{ background: accent }}>
      <Plug className="w-4 h-4" /> {label || t("Analyse verbinden")}
    </button>
  ) : (
    <button onClick={openModal} data-testid="tracking-connect-btn" className="text-xs font-bold inline-flex items-center gap-1.5" style={{ color: accent }}>
      <Plug className="w-3.5 h-3.5" /> {label || t("Analyse verbinden")}
    </button>
  );

  const modal = open ? createPortal(
    <div className="fixed inset-0 z-[11000] flex items-center justify-center p-4" data-testid="tracking-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-lg bg-[#0f0f12] border border-white/10 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0f0f12] z-10">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><Plug className="w-4 h-4" /></span>
            <div>
              <div className="font-bold text-sm">{t("Website-Analyse verbinden")}</div>
              <div className="text-[11px] text-gray-500">{t("KEWAGPage Analytics Light · datenschutzfreundlich")}</div>
            </div>
          </div>
          <button onClick={close} data-testid="tracking-close" className="text-gray-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
          ) : status?.connected ? (
            <div className="text-center py-6" data-testid="tracking-connected">
              <span className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: `${accent}1A`, color: accent }}><ShieldCheck className="w-7 h-7" /></span>
              <div className="font-bold text-lg">{t("Verbunden!")}</div>
              <p className="text-sm text-gray-400 mt-1 max-w-[340px] mx-auto">{t("Ihre Website-Analyse ist aktiv. Besuche, Verlauf und Quellen erscheinen jetzt mit echten Live-Zahlen.")}</p>
              {status.total_hits > 0 && <p className="text-xs text-gray-500 mt-2">{t("Bereits erfasste Aufrufe:")} <b className="text-gray-200">{status.total_hits}</b></p>}
              <button onClick={() => { onConnected && onConnected(); close(); }} data-testid="tracking-done" className="mt-4 inline-flex items-center gap-2 font-bold text-black rounded-xl px-4 py-2.5 text-sm" style={{ background: accent }}>{t("Statistik ansehen")}</button>
            </div>
          ) : (
            <>
              <div className="flex gap-3">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black text-black shrink-0" style={{ background: accent }}>1</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-200">{t("Diesen Code in Ihre Website einfügen")}</div>
                  <p className="text-xs text-gray-500 mt-0.5">{t("Fügen Sie den Code im <head>-Bereich ein (bei WordPress z.B. über ein Header-Snippet-Plugin). Kein eigener Zugriff? KEWAG übernimmt das gerne für Sie.")}</p>
                  <div className="relative mt-2">
                    <pre className="text-[11px] bg-black/50 border border-white/10 rounded-lg p-3 pr-10 overflow-x-auto text-gray-300 whitespace-pre-wrap break-all" data-testid="tracking-snippet">{snippet}</pre>
                    <button onClick={copy} data-testid="tracking-copy" className="absolute top-2 right-2 p-1.5 rounded-md bg-white/5 hover:bg-white/10 text-gray-300 transition-colors">{copied ? <Check className="w-4 h-4 text-[#A3E635]" /> : <Copy className="w-4 h-4" />}</button>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black text-black shrink-0" style={{ background: accent }}>2</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-200">{t("Verbindung prüfen")}</div>
                  <p className="text-xs text-gray-500 mt-0.5">{t("Wir prüfen live, ob der Code gefunden wird — und verbinden automatisch, sobald der erste Besucher kommt.")}</p>
                  <button onClick={verify} disabled={verifying} data-testid="tracking-verify" className="mt-2 inline-flex items-center gap-2 font-bold rounded-xl px-4 py-2.5 text-sm border border-white/15 bg-white/5 hover:bg-white/10 text-white disabled:opacity-60 transition-colors">
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {t("Verbindung prüfen")}
                  </button>
                  {checkedMsg && <p className="text-xs text-amber-300/90 mt-2 flex items-start gap-1.5"><WifiOff className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {checkedMsg}</p>}
                </div>
              </div>

              <div className="pt-4 border-t border-white/10">
                {installTicket ? (
                  <div className="flex items-start gap-2.5" data-testid="tracking-install-done">
                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-200">{t("KEWAG kümmert sich darum.")}</div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t("Wir bauen den Code für Sie ein und melden uns. Sie müssen nichts weiter tun.")}
                        {typeof installTicket === "string" ? ` · ${installTicket}` : ""}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-gray-500 flex-1 min-w-[170px]">{t("Kein eigener Zugriff auf die Website?")}</p>
                    <button onClick={requestInstall} disabled={requesting} data-testid="tracking-request-install"
                      className="inline-flex items-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 border border-white/15 bg-white/5 hover:bg-white/10 text-white disabled:opacity-60 transition-colors">
                      {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LifeBuoy className="w-4 h-4" style={{ color: accent }} />} {t("KEWAG soll das für mich einbauen")}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>, document.body) : null;

  return (<>{Trigger}{modal}</>);
};

export default TrackingConnect;
